import {
    type ReactNode,
    useEffect,
    Fragment,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    Platform,
    type View
} from "react-native";
import type IDropdownMenuRowProps from "./type";
import type {
    DropdownMenuRowWebPropsType
} from "./type";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import {
    useDropdownMenuLevelContext,
    useDropdownMenuContext
} from "../../context";
import {
    normalizeDropdownMenuText,
    focusDropdownMenuElement,
    getDropdownMenuShortcut
} from "../../util";
import {
    getKbdPlatform
} from "../../../kbd/util";
import OptionRow from "../../../optionRow";
import Kbd from "../../../kbd";

const DropdownMenuRow: FC<IDropdownMenuRowProps> = ({
    customTheme: customThemeProp,
    isCloseOnSelect = true,
    isDisabled = false,
    isActive = false,
    onElementChange,
    disabledReason,
    typeaheadText,
    ariaExpanded,
    ariaChecked,
    id: idProp,
    onHoverOut,
    onHoverIn,
    onOpenSub,
    shortcut,
    subTitle,
    trailing,
    leading,
    onPress,
    testID,
    style,
    subId,
    title,
    icon,
    meta,
    role,
    type
}) => {
    const rootContext = useDropdownMenuContext({
        part: "Item"
    });

    const levelContext = useDropdownMenuLevelContext({
        part: "Item"
    });

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(rootContext?.customLocalize);

    const generatedId = useId();

    const rowRef = useRef<View | null>(null);

    const customTheme = customThemeProp ?? rootContext?.customTheme;

    const id = idProp ?? generatedId;

    const hasLeading = !!icon || !!leading;

    const registry = levelContext?.registryRef;

    const handleLeadingChange = levelContext?.handleLeadingChange;

    const textValue = normalizeDropdownMenuText({
        text: typeaheadText ?? title,
        locale: activeLocale
    });

    const shortcutResult = useMemo(() => {
        if(!shortcut) {
            return undefined;
        }

        return getDropdownMenuShortcut({
            platform: getKbdPlatform(),
            keys: shortcut,
            localize
        });
    }, [
        shortcut,
        localize
    ]);

    useEffect(() => {
        const entries = registry?.current;

        if(!entries) {
            return;
        }

        entries.set(id, {
            handleOpenSub: onOpenSub,
            textValue
        });

        return () => {
            entries.delete(id);
        };
    }, [
        onOpenSub,
        textValue,
        registry,
        id
    ]);

    useEffect(() => {
        if(!hasLeading || !handleLeadingChange) {
            return;
        }

        handleLeadingChange(1);

        return () => {
            handleLeadingChange(-1);
        };
    }, [
        handleLeadingChange,
        hasLeading
    ]);

    if(!rootContext || !levelContext) {
        return null;
    }

    const handleRef = (node: View | null) => {
        rowRef.current = node;

        onElementChange?.(node);
    };

    const handlePress = () => {
        onPress?.();

        if(isCloseOnSelect) {
            rootContext.handleClose();
        }
    };

    const onFocus = () => {
        levelContext.handleActiveChange(id);
    };

    const handleHoverIn = () => {
        focusDropdownMenuElement(rowRef.current);

        levelContext.handleItemHover(subId);

        onHoverIn?.();
    };

    const webProps: DropdownMenuRowWebPropsType = Platform.OS === "web" && shortcutResult ? {
        "aria-keyshortcuts": shortcutResult.ariaShortcut
    } : {};

    const trailingNode: ReactNode = shortcutResult ? <Fragment>
        <Kbd
            keys={shortcutResult.labels}
            customTheme={customTheme}
            displayBehaviour="always"
            isDisabled={isDisabled}
            aria-hidden={true}
            separator="auto"
            variant="ghost"
        />
        {trailing}
    </Fragment> : trailing;

    return <OptionRow
        {...webProps}
        description={isDisabled && disabledReason ? disabledReason : subTitle}
        isReserveLeading={levelContext.isReserveLeading && !hasLeading}
        aria-haspopup={ariaExpanded === undefined ? undefined : "menu"}
        tabIndex={levelContext.activeId === id ? 0 : -1}
        aria-expanded={ariaExpanded}
        aria-checked={ariaChecked}
        customTheme={customTheme}
        onHoverIn={handleHoverIn}
        isShowActiveRing={false}
        onHoverOut={onHoverOut}
        isDisabled={isDisabled}
        trailing={trailingNode}
        onPress={handlePress}
        isActive={isActive}
        onFocus={onFocus}
        leading={leading}
        testID={testID}
        ref={handleRef}
        style={style}
        label={title}
        role={role}
        type={type}
        icon={icon}
        meta={meta}
        id={id}
    />;
};
export default DropdownMenuRow;
